<template>
  <Dialog
    :visible="isOpen"
    modal
    :closable="false"
    :dismissableMask="false"
    :draggable="false"
    :style="{ width: 'calc(100vw - 32px)', maxWidth: isMobile ? '358px' : '440px' }"
    :pt="{ root: { class: '!border-0 !shadow-none !bg-transparent' }, mask: { class: '!bg-[rgba(24,24,27,0.48)]' } }"
  >
    <template #container>
      <div
        role="dialog"
        aria-labelledby="whats-new-title"
        class="whats-new bg-white rounded-[20px] flex flex-col max-h-[min(640px,calc(100vh-48px))]"
        dir="rtl"
      >
        <!-- Header: label + date (one update) or count (several), close -->
        <div
          class="flex items-center justify-between flex-none"
          :class="[single ? 'pt-[18px] px-5' : 'pt-[18px] px-5 pb-3.5 border-b border-zinc-100', !isMobile && 'md:px-6']"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="text-[12px] font-semibold text-primary-700 bg-primary-100 px-2.5 py-1 rounded-full flex-none">מה חדש</span>
            <span v-if="single" class="text-[12px] text-zinc-500">{{ formatDate(announcements[0].releasedAt) }}</span>
            <h2 v-else id="whats-new-title" class="m-0 text-[15px] font-semibold text-zinc-900">{{ announcements.length }} עדכונים</h2>
          </div>
          <button
            type="button"
            aria-label="סגירה"
            class="w-11 h-11 -my-2.5 -me-3 flex items-center justify-center rounded-xl text-zinc-500 active:bg-zinc-100 flex-none"
            @click="dismiss('close')"
          >
            <i class="pi pi-times !text-[15px]" />
          </button>
        </div>

        <!-- One update -->
        <div v-if="single" class="pt-3.5 pb-1 px-5 flex flex-col gap-2.5 overflow-y-auto" :class="!isMobile && 'md:px-6'">
          <h2 id="whats-new-title" class="m-0 text-[20px] leading-[1.3] font-semibold text-zinc-900">{{ announcements[0].title }}</h2>
          <p v-for="(paragraph, i) in paragraphs(announcements[0].description)" :key="i" class="body-text">{{ paragraph }}</p>
        </div>

        <!-- Several updates, newest first -->
        <div v-else class="px-5 py-1 overflow-y-auto flex flex-col" :class="!isMobile && 'md:px-6'">
          <section
            v-for="item in announcements"
            :key="item._id"
            class="py-4 flex flex-col gap-1.5 border-b border-zinc-100 last:border-b-0"
          >
            <span class="text-[12px] text-zinc-500">{{ formatDate(item.releasedAt) }}</span>
            <h3 class="m-0 text-[16.5px] leading-[1.35] font-semibold text-zinc-900">{{ item.title }}</h3>
            <p v-for="(paragraph, i) in paragraphs(item.description)" :key="i" class="body-text !text-[14px]">{{ paragraph }}</p>
          </section>
        </div>

        <div
          class="flex-none"
          :class="[single ? 'p-5' : 'pt-4 px-5 pb-5 border-t border-zinc-100', !isMobile && 'md:pb-6 md:px-6 md:flex md:justify-start']"
        >
          <button
            type="button"
            class="w-full h-12 rounded-[14px] bg-primary text-white text-[15px] font-semibold active:opacity-90"
            :class="!isMobile && 'md:w-auto md:min-w-[140px] md:h-11 md:px-6 md:rounded-xl'"
            @click="dismiss('got_it')"
          >הבנתי</button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { DateTime } from 'luxon';
import { IAnnouncement } from '@torim-app/types';
import { trackEvent } from '@app/composables/posthog';
import { useResponsive } from '@app/composables/responsive';

/**
 * "What's new" — text-only updates from the DB (see IAnnouncement). One update shows as a
 * single card; several stack in one scrollable card, newest first, dismissed together.
 */
const props = defineProps<{
  announcements: IAnnouncement[];
  isOpen: boolean;
}>();

const emit = defineEmits<{ dismiss: [] }>();

const { isMobile } = useResponsive();

const single = computed(() => props.announcements.length === 1);

const formatDate = (date: Date | string) =>
  DateTime.fromJSDate(new Date(date)).setZone('Asia/Jerusalem').setLocale('he').toFormat('d בMMMM yyyy');

// Plain text: blank lines separate paragraphs
const paragraphs = (text: string) =>
  (text ?? '').split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);

watch(() => props.isOpen, (open) => {
  if (open) trackEvent('whats_new_modal_shown', { count: props.announcements.length });
}, { immediate: true });

const dismiss = (via: 'got_it' | 'close') => {
  trackEvent('whats_new_modal_dismissed', { count: props.announcements.length, via });
  emit('dismiss');
};
</script>

<style scoped lang="scss">
.whats-new {
  font-family: 'Rubik', system-ui, sans-serif;
  box-shadow: 0 20px 48px rgba(24, 24, 27, 0.18);
}

.body-text {
  margin: 0;
  font-size: 14.5px;
  line-height: 1.65;
  color: #3f3f46;
  white-space: pre-line;
}
</style>
